import exceptionIcon from '@jetbrains/icons/exception.svg'
import ErrorMessage from '@jetbrains/ring-ui/components/error-message/error-message'
import sortBy from 'lodash/sortBy'
import React, {memo, useEffect} from 'react'
import {graphql} from 'react-relay'

import {useRefetchableLazyLoadQuery} from '../../../../../hooks/relay/useRefetchableLazyLoadQuery'
import Routes, {getHref} from '../../../../../routes'
import {
  defaultThrottleTime,
  getBuildTypeSuffix,
  getTopics,
  subscribeController,
} from '../../../../../utils/subscriber'
import {
  BUILD_FINISHED,
  BUILD_INTERRUPTED,
  BUILD_STARTED,
  BUILD_TYPE_ADDED_TO_QUEUE,
  BUILD_TYPE_REMOVED_FROM_QUEUE,
} from '../../../../../utils/subscriptionEvents'
import Loader from '../../../../common/Loader/Loader'
import Redirect from '../../../../common/Redirect/Redirect'
import {hasPipelineHeadParameter} from '../../EditPipelinePage/EditPipelinePage.utils'
import {pipelinesApi} from '../../services/pipelinesApi'
import {serializeError} from '../../utils/error'

import type {PipelineListQuery} from './__generated__/PipelineListQuery.graphql'
import {PipelineLine} from './PipelineLine/PipelineLine'

import styles from './PipelineList.css'

const query = graphql`
  query PipelineListQuery($locator: String!) {
    projectsRest(locator: $locator) {
      project {
        buildTypes {
          buildType {
            parameters {
              property {
                name
              }
            }
            internalId
          }
        }
        internalId
        ...PipelineLineProjectFragment
      }
    }
  }
`

type Props = {
  projectsLocator: string
}
function PipelineList({projectsLocator}: Props) {
  const {pipelinesLoading, pipelinesError, pipelines} =
    pipelinesApi.endpoints.getAllPipelines.useQuery(undefined, {
      selectFromResult: ({isLoading, error, data}) => ({
        pipelinesLoading: isLoading,
        pipelinesError: error,
        pipelines: data,
      }),
    })
  const [{projectsRest}, refetchProjects] = useRefetchableLazyLoadQuery<PipelineListQuery>(query, {
    locator: projectsLocator,
  })
  const projects = projectsRest?.project

  useEffect(() => {
    if (projects == null || projects.length === 0) {
      return undefined
    }
    return subscribeController(
      projects.flatMap(project => {
        const headInternalId = project.buildTypes?.buildType?.find(job =>
          hasPipelineHeadParameter(job.parameters),
        )?.internalId
        return headInternalId != null
          ? getTopics(getBuildTypeSuffix(headInternalId), [
              BUILD_TYPE_ADDED_TO_QUEUE,
              BUILD_TYPE_REMOVED_FROM_QUEUE,
              BUILD_STARTED,
              BUILD_FINISHED,
              BUILD_INTERRUPTED,
            ])
          : []
      }),
      refetchProjects,
      defaultThrottleTime,
    )
  }, [pipelines, projects, refetchProjects])

  if (pipelinesLoading) {
    return <Loader />
  }

  if (pipelinesError) {
    return (
      <ErrorMessage
        icon={exceptionIcon}
        message="Error while loading project list"
        description={serializeError(pipelinesError)}
      />
    )
  }

  if (pipelines?.length === 0) {
    return <Redirect to={getHref(Routes.CREATE_PIPELINE)} />
  }

  return (
    <div className={styles.list}>
      {sortBy(pipelines, ({settings}) => settings.name)?.map(({id}, i) => (
        <PipelineLine
          pipelineId={id!}
          key={id}
          index={i}
          projectKey={projects?.find(project => project.internalId === id) ?? null}
        />
      ))}
    </div>
  )
}

export default memo(PipelineList)
